/**
 * 1. Login / Auth — email/password sign-in. An account with a second factor
 * continues at /login/mfa (MfaChallengePage); one with no workspace goes to
 * /create-workspace. One that must set up a second factor first (W2.8b: a
 * privileged role, or a workspace that requires it for everyone) gets an
 * enrolment token instead of a session, and sets it up right here
 * (FactorEnrolment); confirming opens the workspace. Never a dead end.
 *
 * There is no SSO button and no password-reset link: neither had anything
 * behind it (the button had no handler, and /forgot-password is neither a
 * route here nor an endpoint in the API). They come back with the feature.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, Field, Input, PageHeader } from "@/components/ui";
import { post } from "@/lib/api/client";
import { whileSigningIn } from "@/lib/api/sign-in";
import { useAuthStore } from "@/lib/auth/store";
import { translateNotice } from "@/lib/auth/messages";
import { errorMessage, useTranslation } from "@/lib/i18n";
import { LanguageSwitch } from "./LanguageSwitch";
import { FactorEnrolment, type Enrolled } from "./FactorEnrolment";

interface LoginResponse {
  accessToken: string;
  mfaRequired?: boolean;
  mfaToken?: string;
  /** W2.8b: no session; set up two-step sign-in with this token first. */
  mfaEnrollmentRequired?: boolean;
  enrollmentToken?: string;
  tenants: Array<{ code: string; name: string }>;
  claims: {
    sub: string;
    tenant_id: string;
    cell_id: string;
    zone_id: string;
    tenant_user_id: string;
    roles: string[];
    permissions: string[];
    exp: number;
    iat: number;
    jti: string;
  };
}

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const setActiveTenantCode = useAuthStore((s) => s.setActiveTenantCode);
  // Left by the API client when the server ended the session
  // (401 SESSION_REVOKED); cleared when a new session starts.
  const sessionNotice = useAuthStore((s) => s.sessionNotice);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [enrolWith, setEnrolWith] = useState<string | null>(null);

  /** Enrolled: the session the confirmation opened, adopted as sign-in's. */
  const enter = (result: Enrolled) => {
    setSession(
      result.accessToken,
      result.claims,
      result.activeTenantCode || undefined,
    );
    navigate(result.activeTenantCode ? "/app/home" : "/create-workspace", {
      replace: true,
    });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // Holds the refresh lock: a sign-in replaces this browser's refresh
      // cookie and ends the session it named (D1042, lib/api/sign-in.ts).
      const res = await whileSigningIn(() =>
        post<LoginResponse>("/auth/login", { email, password }),
      );
      if (res.mfaEnrollmentRequired && res.enrollmentToken) {
        setEnrolWith(res.enrollmentToken);
        return;
      }
      if (res.mfaRequired) {
        // The challenge token is what the next step authenticates with; the
        // flag alone left that page with nothing to send, which is why the
        // route never worked.
        useAuthStore.getState().setMfaRequired(true);
        useAuthStore.getState().setMfaChallenge(res.mfaToken ?? null);
        navigate("/login/mfa");
        return;
      }
      setSession(res.accessToken, res.claims);
      const first = res.tenants[0];
      if (first) setActiveTenantCode(first.code);
      navigate(first ? "/app/home" : "/create-workspace");
    } catch (err) {
      setError(errorMessage(err, t, i18n.language));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{ inlineSize: 400, maxInlineSize: "100%" }}>
        <div style={{ textAlign: "center", marginBlockEnd: 20 }}>
          <img src="/icons/atlas.svg" alt="" width="48" height="48" />
          <h1 style={{ margin: "8px 0 0" }}>{t("auth.login.heading")}</h1>
        </div>
        <Card>
          {enrolWith ? (
            <FactorEnrolment enrollmentToken={enrolWith} onEnrolled={enter} />
          ) : (
            <>
              <PageHeader
                title={t("auth.login.title")}
                subtitle={t("auth.login.subtitle")}
              />
              {sessionNotice && (
                <div
                  role="status"
                  style={{ fontSize: 13, marginBlockEnd: 12 }}
                  data-testid="session-notice"
                >
                  {translateNotice(sessionNotice, t)}
                </div>
              )}
              <form onSubmit={submit}>
                <Field label={t("auth.login.email")} required>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </Field>
                <Field label={t("auth.login.password")} required>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                </Field>
                {error && (
                  <div
                    role="alert"
                    style={{
                      color: "var(--atlas-danger)",
                      fontSize: 13,
                      marginBlockEnd: 12,
                    }}
                  >
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  style={{ inlineSize: "100%" }}
                >
                  {loading
                    ? t("auth.login.submitting")
                    : t("auth.login.submit")}
                </Button>
              </form>
              <div
                style={{
                  marginBlockStart: 16,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  fontSize: 13,
                }}
              >
                <LanguageSwitch />
                <Link to="/signup">{t("auth.login.createWorkspace")}</Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
